/**
 * assets/diamond-search-scrollbar.js
 *
 * Desktop custom scrollbar visualization / interaction for
 * aside#diamondSearchSidebar, plus the small More Filters continuation
 * nudge. Extracted from sections/diamond-search.liquid to stay under
 * Shopify's 256 KB section template limit.
 *
 * Native overflow-y:auto remains the scroll mechanism. Track/thumb are a
 * proportional interactive representation. No wheel intercept.
 */
(function () {
  "use strict";

/* Desktop visual scrollbar for aside#diamondSearchSidebar.
   Native overflow-y:auto remains the scroll mechanism; track/thumb
   are a proportional interactive representation. No wheel intercept. */
function initDesktopSidebarScrollbar() {
  var sidebar = document.getElementById("diamondSearchSidebar");
  var rail = sidebar && sidebar.querySelector(".diamond-search__sb-scroll");
  var track = rail && rail.querySelector(".diamond-search__sb-track");
  var thumb = rail && rail.querySelector(".diamond-search__sb-thumb");
  if (!sidebar || !rail || !track || !thumb) return;

  var mq = window.matchMedia("(min-width: 990px)");
  var raf = 0;
  var TRACK_INSET = 4;
  var drag = null;

  function getMetrics() {
    var clientH = sidebar.clientHeight;
    var scrollH = sidebar.scrollHeight;
    var overflowing = scrollH > clientH + 1 && clientH > 0;
    var trackH = Math.max(0, clientH - TRACK_INSET * 2);
    var thumbH = overflowing ? Math.max(16, (clientH / scrollH) * trackH) : trackH;
    var maxScroll = Math.max(0, scrollH - clientH);
    var maxTravel = Math.max(0, trackH - thumbH);
    return {
      clientH: clientH,
      scrollH: scrollH,
      overflowing: overflowing,
      trackH: trackH,
      thumbH: thumbH,
      maxScroll: maxScroll,
      maxTravel: maxTravel
    };
  }

  function update() {
    raf = 0;
    if (!mq.matches) {
      rail.hidden = true;
      return;
    }

    var m = getMetrics();
    if (!m.overflowing) {
      rail.hidden = true;
      return;
    }

    var thumbY = m.maxScroll > 0
      ? (sidebar.scrollTop / m.maxScroll) * m.maxTravel
      : 0;

    sidebar.style.setProperty("--ds-sb-track-h", Math.round(m.trackH) + "px");
    sidebar.style.setProperty("--ds-sb-thumb-h", Math.round(m.thumbH) + "px");
    sidebar.style.setProperty("--ds-sb-thumb-y", Math.round(thumbY) + "px");
    rail.hidden = false;
  }

  function schedule() {
    if (raf) return;
    raf = window.requestAnimationFrame(update);
  }

  function clampScrollTop(value) {
    var maxScroll = Math.max(0, sidebar.scrollHeight - sidebar.clientHeight);
    return Math.max(0, Math.min(maxScroll, value));
  }

  function startDrag(e, startScrollTop, ratio) {
    drag = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startScrollTop: startScrollTop,
      ratio: ratio
    };
    thumb.classList.add("is-dragging");
    sidebar.classList.add("is-sb-dragging");
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {}
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  }

  function onDragMove(e) {
    if (!drag || e.pointerId !== drag.pointerId) return;
    sidebar.scrollTop = clampScrollTop(
      drag.startScrollTop + (e.clientY - drag.startY) * drag.ratio
    );
  }

  function onDragEnd(e) {
    if (!drag || (e && e.pointerId !== drag.pointerId)) return;
    drag = null;
    thumb.classList.remove("is-dragging");
    sidebar.classList.remove("is-sb-dragging");
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragEnd);
    window.removeEventListener("pointercancel", onDragEnd);
  }

  thumb.addEventListener("pointerdown", function (e) {
    if (e.button !== 0 || !mq.matches) return;
    e.preventDefault();
    e.stopPropagation();
    var m = getMetrics();
    if (!m.overflowing) return;
    startDrag(e, sidebar.scrollTop, m.maxTravel > 0 ? m.maxScroll / m.maxTravel : 0);
  });

  /* Track click maps to a proportional scroll position (thumb centered
     on the click), then continues as a drag if the pointer moves. */
  track.addEventListener("pointerdown", function (e) {
    if (e.button !== 0 || !mq.matches) return;
    if (e.target === thumb || thumb.contains(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    var m = getMetrics();
    if (!m.overflowing) return;

    var rect = track.getBoundingClientRect();
    var clickY = e.clientY - rect.top;
    var thumbY = Math.max(0, Math.min(m.maxTravel, clickY - m.thumbH / 2));
    sidebar.scrollTop = m.maxTravel > 0
      ? (thumbY / m.maxTravel) * m.maxScroll
      : 0;

    startDrag(e, sidebar.scrollTop, m.maxTravel > 0 ? m.maxScroll / m.maxTravel : 0);
  });

  sidebar.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", schedule);
  }
  if (mq.addEventListener) mq.addEventListener("change", schedule);
  else if (mq.addListener) mq.addListener(schedule);

  sidebar.querySelectorAll("details").forEach(function (details) {
    details.addEventListener("toggle", schedule);
  });

  if (window.ResizeObserver) {
    var ro = new ResizeObserver(schedule);
    Array.prototype.forEach.call(sidebar.children, function (child) {
      if (child !== rail) ro.observe(child);
    });
  }

  update();
}

  /* How far to increment the sidebar's own scrollTop so a target (plus
     cuePx of its height) sits above the scrollport content bottom.
     Never moves a keep-visible header above the content top. Never
     touches window.scrollY. */
  function computeMoreFiltersRevealDelta(metrics) {
    var needed = Math.min(metrics.cuePx, Math.max(0, metrics.targetHeight));
    var delta = metrics.targetTop + needed - metrics.contentBottom;
    if (!(delta > 1)) return 0;
    if (metrics.keepVisibleTop != null) {
      var maxDelta = Math.max(0, metrics.keepVisibleTop - metrics.contentTop);
      if (delta > maxDelta) delta = maxDelta;
    }
    return delta;
  }

  /* Desktop only: when More Filters (or a nested group) opens near the
     bottom of the sidebar scrollport, nudge the existing sidebar
     scrollTop so the newly revealed content is visible. Does not change
     sidebar max-height, custom scrollbar, accordion state, or mobile. */
  function initMoreFiltersContinuationNudge() {
    var moreFiltersAccordion = document.getElementById("moreFiltersAccordion");
    if (!moreFiltersAccordion) return;

    var nestedCueMq = window.matchMedia("(min-width: 990px)");
    var NESTED_OPEN_CUE_PX = 36;

    function sidebarContentBox(sidebar) {
      var sidebarRect = sidebar.getBoundingClientRect();
      var cs = window.getComputedStyle(sidebar);
      return {
        top:
          sidebarRect.top +
          (parseFloat(cs.borderTopWidth) || 0) +
          (parseFloat(cs.paddingTop) || 0),
        bottom:
          sidebarRect.bottom -
          (parseFloat(cs.borderBottomWidth) || 0) -
          (parseFloat(cs.paddingBottom) || 0)
      };
    }

    function revealInSidebar(sidebar, target, cuePx, keepVisible) {
      if (!sidebar || !target) return;
      window.requestAnimationFrame(function () {
        var box = sidebarContentBox(sidebar);
        var targetRect = target.getBoundingClientRect();
        var keepRect = keepVisible ? keepVisible.getBoundingClientRect() : null;
        var delta = computeMoreFiltersRevealDelta({
          contentTop: box.top,
          contentBottom: box.bottom,
          targetTop: targetRect.top,
          targetHeight: targetRect.height,
          cuePx: cuePx,
          keepVisibleTop: keepRect ? keepRect.top : null
        });
        if (delta > 1) sidebar.scrollTop += delta;
      });
    }

    function revealNestedAccordionCue(details) {
      if (!nestedCueMq.matches || !details.open) return;
      var sidebar = document.getElementById("diamondSearchSidebar");
      var body = details.querySelector(":scope > .diamond-search__accordion-body");
      if (!sidebar || !body) return;
      revealInSidebar(sidebar, body, NESTED_OPEN_CUE_PX, null);
    }

    function revealParentMoreFiltersCue() {
      if (!nestedCueMq.matches || !moreFiltersAccordion.open) return;
      var sidebar = document.getElementById("diamondSearchSidebar");
      var firstNested = moreFiltersAccordion.querySelector(
        ":scope > .diamond-search__more-filters-body details.diamond-search__accordion--nested"
      );
      var firstSummary = firstNested && firstNested.querySelector(":scope > summary");
      var keepVisible = moreFiltersAccordion.querySelector(":scope > summary");
      if (!sidebar || !firstSummary) return;
      revealInSidebar(sidebar, firstSummary, Infinity, keepVisible);
    }

    moreFiltersAccordion.addEventListener("toggle", revealParentMoreFiltersCue);

    moreFiltersAccordion.querySelectorAll("details.diamond-search__accordion--nested").forEach(function (details) {
      details.addEventListener("toggle", function () {
        revealNestedAccordionCue(details);
      });
    });
  }

  function boot() {
    initDesktopSidebarScrollbar();
    initMoreFiltersContinuationNudge();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    if (!event.target || !event.target.querySelector) return;
    if (event.target.querySelector("#diamondSearchSidebar")) boot();
  });
})();
